import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  FormGroup,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItemText,
  Alert,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Edit, Delete } from "@mui/icons-material";
import React, { useEffect } from "react";

const ProfessionalDetailsStep = ({
  profileData,
  handleInputChange,
  currentExperience,
  handleCurrentExperienceChange,
  currentInternship,
  handleCurrentInternshipChange,
  editingExperienceIndex,
  editingInternshipIndex,
  newResponsibility,
  setNewResponsibility,
  newInternshipResponsibility,
  setNewInternshipResponsibility,
  handleAddResponsibility,
  handleAddInternshipResponsibility,
  handleRemoveResponsibility,
  handleRemoveInternshipResponsibility,
  handleAddOrUpdateExperience,
  handleAddOrUpdateInternship,
  handleEditExperience,
  handleEditInternship,
  handleDeleteExperience,
  handleDeleteInternship,
  validationErrors,
}) => {
  const theme = useTheme(); // CHANGED: Instantiate theme
  const { is_fresher, has_internship, is_experienced, employee_level } =
    profileData;

  useEffect(() => {
    let newLevel;
    if (is_experienced) {
      newLevel = "experienced";
    } else if (has_internship) {
      newLevel = "internship";
    } else {
      newLevel = "fresher";
    }

    if (!employee_level || employee_level[0] !== newLevel) {
      handleInputChange("employee_level", [newLevel]);
    }
  }, [
    is_fresher,
    has_internship,
    is_experienced,
    employee_level,
    handleInputChange,
  ]);

  return (
    // CHANGED: Replaced hardcoded color with theme variable
    <Box sx={{ bgcolor: "background.paper", p: 0 }}>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select your employment status and add relevant experience details. At
        least one form of experience (internship or professional) is required if
        not a fresher.
      </Typography>

      {validationErrors.professional && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validationErrors.professional}
        </Alert>
      )}

      {/* CHANGED: Replaced hardcoded colors with theme variables */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          bgcolor: "background.paper",
          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          borderRadius: "20px",
        }}
      >

        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: "bold" }}
        >
          Employment Status
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={profileData.is_fresher}
                onChange={(e) => {
                  const isFresher = e.target.checked;
                  handleInputChange("is_fresher", isFresher);
                  if (isFresher) {
                    handleInputChange("is_experienced", false);
                    handleInputChange("has_internship", false);
                  }
                }}
                name="isFresher"
                color="primary" // CHANGED: Use color prop
              />
            }
            label="Fresher"
          />
          <FormControlLabel
            control={
              <Switch
                checked={profileData.has_internship}
                onChange={(e) => {
                  const hasInternship = e.target.checked;
                  handleInputChange("has_internship", hasInternship);
                  if (hasInternship) {
                    handleInputChange("is_fresher", false);
                  }
                }}
                name="hasInternship"
                disabled={profileData.is_fresher}
                color="primary" // CHANGED: Use color prop
              />
            }
            label="Internship Experience"
          />
          <FormControlLabel
            control={
              <Switch
                checked={profileData.is_experienced}
                onChange={(e) => {
                  const isExperienced = e.target.checked;
                  handleInputChange("is_experienced", isExperienced);
                  if (isExperienced) {
                    handleInputChange("is_fresher", false);
                  }
                }}
                name="isExperienced"
                disabled={profileData.is_fresher}
                color="primary" // CHANGED: Use color prop
              />
            }
            label="Professional Experience"
          />
        </FormGroup>
      </Paper>
      {profileData.is_fresher && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            border: theme => `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            borderRadius: "20px",
          }}
        >

          <Typography color="text.secondary">
            As a fresher, you can skip this section. Toggle on internship or
            professional experience if applicable.
          </Typography>
        </Paper>
      )}
      {(profileData.has_internship || profileData.is_experienced) && (
        <Grid container spacing={3}>
          {profileData.has_internship && (
            <Grid item xs={12} md={profileData.is_experienced ? 6 : 12}>
              <Stack spacing={4}>
                {/* CHANGED: Replaced hardcoded color with theme variable */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: "background.paper",
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                    borderRadius: "20px",
                  }}
                >

                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", fontWeight: "bold" }}
                  >
                    {editingInternshipIndex !== null
                      ? "Edit Internship"
                      : "Add New Internship"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Internship Title"
                        value={currentInternship.title}
                        onChange={(e) =>
                          handleCurrentInternshipChange("title", e.target.value)
                        }
                        required
                        error={!!validationErrors.internship_title}
                        helperText={validationErrors.internship_title || ""}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={currentInternship.company}
                        onChange={(e) =>
                          handleCurrentInternshipChange(
                            "company",
                            e.target.value
                          )
                        }
                        required
                        error={!!validationErrors.internship_company}
                        helperText={validationErrors.internship_company || ""}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Duration"
                        value={currentInternship.duration}
                        onChange={(e) =>
                          handleCurrentInternshipChange(
                            "duration",
                            e.target.value
                          )
                        }
                        required
                        error={!!validationErrors.internship_duration}
                        helperText={
                          validationErrors.internship_duration ||
                          "e.g., Jun 2023 - Aug 2023"
                        }
                      />
                    </Grid>
                  </Grid>
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      label="Add Responsibilities..."
                      multiline
                      rows={4}
                      value={newInternshipResponsibility}
                      onChange={(e) =>
                        setNewInternshipResponsibility(e.target.value)
                      }
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleAddOrUpdateInternship}
                    color="primary"
                    sx={{
                      mt: 3,
                      borderRadius: "12px",
                      px: 4,
                      boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {editingInternshipIndex !== null ? "Update" : "Add"} Internship
                  </Button>

                </Paper>
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", fontWeight: "bold" }}
                  >
                    Your Internships ({profileData.internships?.length || 0})
                  </Typography>
                  {profileData.internships.map((intern, index) => (
                    <Card
                      key={index}
                      elevation={0}
                      sx={{
                        mb: 2,
                        bgcolor: "background.paper",
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: "16px",
                      }}
                    >

                      <CardHeader
                        title={intern.title}
                        subheader={`${intern.company} | ${intern.duration}`}
                        titleTypographyProps={{
                          color: "primary.main", // CHANGED
                          fontWeight: "bold",
                        }}
                        subheaderTypographyProps={{ color: "text.secondary" }} // CHANGED
                        action={
                          <>
                            <IconButton
                              onClick={() => handleEditInternship(index)}
                              color="success" // CHANGED: Use color prop
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteInternship(index)}
                              color="error" // CHANGED: Use color prop
                            >
                              <Delete />
                            </IconButton>
                          </>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <List dense>
                          {(intern.responsibilities || []).map(
                            (resp, rIndex) => (
                              <ListItemText
                                key={rIndex}
                                primary={`• ${resp}`}
                                primaryTypographyProps={{
                                  color: "text.primary",
                                }} // CHANGED
                              />
                            )
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Stack>
            </Grid>
          )}
          {profileData.is_experienced && (
            <Grid item xs={12}>
              <Stack spacing={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: "background.paper",
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                    borderRadius: "20px",
                  }}
                >

                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", fontWeight: "bold" }}
                  >
                    {editingExperienceIndex !== null
                      ? "Edit Experience"
                      : "Add New Experience"}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Job Title"
                        value={currentExperience.title}
                        onChange={(e) =>
                          handleCurrentExperienceChange("title", e.target.value)
                        }
                        required
                        error={!!validationErrors.experience_title}
                        helperText={validationErrors.experience_title || ""}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={currentExperience.company}
                        onChange={(e) =>
                          handleCurrentExperienceChange(
                            "company",
                            e.target.value
                          )
                        }
                        required
                        error={!!validationErrors.experience_company}
                        helperText={validationErrors.experience_company || ""}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Duration in Month"
                        value={currentExperience.duration}
                        onChange={(e) =>
                          handleCurrentExperienceChange(
                            "duration",
                            e.target.value
                          )
                        }
                        required
                        error={!!validationErrors.experience_duration}
                        helperText={validationErrors.experience_duration || ""}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Add Responsibilities........"
                        multiline
                        rows={4}
                        value={newResponsibility}
                        onChange={(e) => setNewResponsibility(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleAddOrUpdateExperience}
                        color="primary"
                        sx={{
                          borderRadius: "12px",
                          px: 4,
                          boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                      >
                        Add Experience
                      </Button>

                    </Grid>
                  </Grid>
                </Paper>
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", fontWeight: "bold" }}
                  >
                    Your Experience ({profileData.experience?.length || 0})
                  </Typography>
                  {profileData.experience.map((exp, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        bgcolor: "background.paper",
                        boxShadow: "none",
                      }}
                    >
                      <CardHeader
                        title={exp.title}
                        subheader={`${exp.company} | ${exp.duration}`}
                        titleTypographyProps={{
                          color: "primary.main", // CHANGED
                          fontWeight: "bold",
                        }}
                        subheaderTypographyProps={{ color: "text.secondary" }} // CHANGED
                        action={
                          <>
                            <IconButton
                              onClick={() => handleEditExperience(index)}
                              color="success" // CHANGED
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteExperience(index)}
                              color="error" // CHANGED
                            >
                              <Delete />
                            </IconButton>
                          </>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <List dense>
                          {(exp.responsibilities || []).map((resp, rIndex) => (
                            <ListItemText
                              key={rIndex}
                              primary={`• ${resp}`}
                              primaryTypographyProps={{ color: "text.primary" }} // CHANGED
                            />
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ProfessionalDetailsStep;
